import { useState } from "react";
import { Button, Modal, ModalFooter, Notice, Steps } from "@plataforma/design-system";
import { PASSWORD_MIN_LENGTH, type FeatureKey, type PublicAppConfig } from "@plataforma/shared";
import { useAppConfig } from "./config";

export interface HelpTopic {
  title: string;
  summary: string;
  sections: { heading: string; steps: string[] }[];
  notes?: string[];
}

interface HelpContext {
  config: PublicAppConfig;
  has: (feature: FeatureKey) => boolean;
  /** "cliente" ou "paciente", conforme a aplicação. */
  client: string;
  clients: string;
}

/**
 * Ajuda de cada tela. O texto se adapta à aplicação: usa os termos dela e só fala
 * de funcionalidades que estão ligadas. Os nomes de botões aqui devem ser os da tela.
 */
export const helpTopics = {
  login: () => ({
    title: "Como entrar",
    summary: "O acesso é individual: cada pessoa da equipe tem o seu e-mail e a sua senha.",
    sections: [
      {
        heading: "Entrar",
        steps: ["Digite o e-mail e a senha que você recebeu.", "Clique em “Entrar”.", "Ao terminar o trabalho, use “Sair”, principalmente em computador compartilhado."],
      },
    ],
    notes: [
      "Depois de várias senhas erradas seguidas, o acesso fica bloqueado por alguns minutos. Aguarde e tente de novo.",
      "A sessão expira sozinha depois de algumas horas; basta entrar de novo.",
      "Esqueceu a senha? Peça uma nova a quem administra o sistema. Ainda não há recuperação automática.",
    ],
  }),

  account: () => ({
    title: "Como usar as configurações pessoais",
    summary: "Esta tela é só sua: mostra os dados do seu acesso e permite trocar a sua senha.",
    sections: [
      {
        heading: "Trocar a senha",
        steps: ["Digite a senha atual.", `Digite a nova senha, com pelo menos ${PASSWORD_MIN_LENGTH} caracteres, e repita para confirmar.`, "Clique em “Trocar senha”."],
      },
    ],
    notes: [
      "Ao trocar a senha, você continua neste aparelho, mas os outros lugares onde a sua conta estava aberta são desconectados.",
      "Use uma senha que você não usa em outros sites. Uma frase com várias palavras é fácil de lembrar e difícil de adivinhar.",
      "Para mudar o nome ou o e-mail do acesso, peça a quem administra o sistema.",
    ],
  }),

  agenda: ({ has, client }) => ({
    title: "Como usar a agenda",
    summary: "A agenda mostra as consultas do dia ou da semana. O horário é combinado caso a caso: o sistema avisa conflitos, mas não impede marcar.",
    sections: [
      {
        heading: "Marcar uma consulta",
        steps: [
          "Clique em “＋ Nova consulta”. Na visão de semana, o “＋” de cada dia já abre com a data certa.",
          `Busque o ${client} pelo nome ou telefone. Se for novo, escolha “＋ Cadastrar…” e informe nome e telefone ali mesmo.`,
          "Escolha o atendimento e o local, a data, o horário, a duração e o valor combinado. Use 0 para atendimento sem cobrança.",
          "Se aparecer um aviso de conflito ou de horário fora do atendimento do local, confira. Você pode salvar mesmo assim.",
          "Clique em “Marcar consulta”.",
        ],
      },
      {
        heading: "Acompanhar o dia",
        steps: [
          "Use as setas e o botão “Hoje” para mudar de data, e “Dia” ou “Semana” para trocar a visão.",
          "Na linha da consulta, o botão à direita faz o próximo passo: “Confirmar” e, no dia, “Realizada”.",
          "Clique na consulta para ver os detalhes, abrir o WhatsApp, mudar a situação (inclusive cancelar ou marcar que não compareceu) ou “Editar / remarcar”.",
          ...(has("invoices") ? ["Quando a consulta é marcada como realizada, a nota fiscal fica pendente e o botão “Emitir nota” aparece na própria linha."] : []),
        ],
      },
    ],
    notes: ["A bolinha colorida indica o local de atendimento; a legenda fica no rodapé da agenda.", "Remarcar não apaga nada: a consulta muda de data e o histórico da alteração fica guardado."],
  }),

  clients: ({ has, client, clients }) => ({
    title: `Como usar o cadastro de ${clients}`,
    summary: `O cadastro guarda só o necessário para contato e para a nota fiscal. Informações clínicas ficam no prontuário, fora deste sistema.`,
    sections: [
      {
        heading: `Encontrar ou cadastrar um ${client}`,
        steps: [
          "Digite parte do nome, do telefone ou do e-mail no campo de filtro.",
          "Clique na linha para abrir o perfil.",
          "Para um cadastro novo, use “＋ Novo cadastro”. Nome e telefone são obrigatórios; e-mail e CPF são opcionais.",
        ],
      },
    ],
    notes: [
      "Cada telefone só pode estar em um cadastro; o sistema avisa se já existir.",
      ...(has("agenda") ? ["As colunas “Última consulta” e “Próxima consulta” vêm da agenda: a última conta só consultas realizadas."] : []),
      "O CPF pode ficar em branco e ser preenchido depois, na hora de emitir a nota.",
    ],
  }),

  "client-profile": ({ has, client }) => ({
    title: `Como usar o perfil do ${client}`,
    summary: `O perfil reúne o contato, o histórico de consultas e as notas administrativas de um ${client}.`,
    sections: [
      {
        heading: "O que dá para fazer aqui",
        steps: [
          "“Abrir WhatsApp” inicia a conversa com o telefone do cadastro. O sistema nunca envia mensagens sozinho.",
          "“Editar cadastro” corrige nome, telefone, e-mail ou CPF.",
          ...(has("agenda") ? ["“＋ Marcar consulta” abre a agenda já com este cadastro escolhido."] : []),
          "“＋ Nota” registra uma preferência de contato ou de agenda, por exemplo o melhor horário para ligar.",
        ],
      },
    ],
    notes: ["As notas são administrativas e ficam visíveis para toda a equipe. Não registre sintomas, diagnósticos, exames nem condutas."],
  }),

  invoices: ({ config, has }) => ({
    title: "Como usar as notas fiscais",
    summary: "Cada consulta realizada gera uma nota pendente. Aqui você emite, consulta e cancela as notas.",
    sections: [
      {
        heading: "Emitir uma nota",
        steps: [
          "Na lista “Pendentes”, clique em “Emitir nota”.",
          "Confira o CPF do tomador, o valor e a descrição do serviço. O CPF informado fica salvo no cadastro.",
          "Clique em “Emitir nota”. Depois de emitida, use “Próxima pendente” para seguir pela fila.",
          "Se a emissão falhar, nada é emitido: a nota continua pendente e você pode usar “Tentar de novo”.",
        ],
      },
      {
        heading: "Depois de emitir",
        steps: [
          "Em “Emitidas” ou “Todas”, clique em “Ver nota” para consultar os dados e avisar o cliente pelo WhatsApp.",
          "Para corrigir um erro, use “Cancelar nota”, informe o motivo e depois “Emitir nota substituta”.",
          "Se a nota foi emitida direto no site do governo, use “Registrar manualmente” para guardar o número aqui.",
          ...(has("reports") ? ["“Relatório para a contabilidade” abre as notas do mês, prontas para imprimir ou exportar."] : []),
        ],
      },
    ],
    notes: [
      ...(has("settings") ? ["Para emitir é preciso ter um certificado digital A1 válido instalado em Configurações."] : []),
      ...(config.settings.nfseIssuer === "simulated" ? ["O emissor desta aplicação está em modo simulado: as notas recebem número iniciado em SIM, não são enviadas ao governo e não têm validade fiscal."] : []),
      "Nota emitida não significa pagamento recebido; o sistema não controla recebimentos.",
    ],
  }),

  finance: ({ has }) => ({
    title: "Como usar o financeiro",
    summary: "O financeiro compara o que entrou com o que saiu em cada mês e mostra um resultado estimado.",
    sections: [
      {
        heading: "Ler os números",
        steps: [
          "Escolha o período: últimos meses, últimos dias ou um intervalo personalizado.",
          has("invoices") ? "A receita soma as notas fiscais emitidas, pela data de emissão, com as receitas extras." : "A receita soma as receitas registradas no período.",
          "O resultado estimado é a receita menos as despesas lançadas. O gráfico e a tabela mostram mês a mês.",
        ],
      },
      {
        heading: "Lançar receitas e despesas",
        steps: [
          has("invoices") ? "Em “＋ Registrar receita”, lance só o que não está numa nota fiscal deste sistema, como repasses de clínica ou plano, para não contar duas vezes." : "Em “＋ Registrar receita”, informe origem, descrição, valor e data.",
          "Em “＋ Registrar despesa”, escolha “Pontual” para um gasto único ou “Mensal” para repetir todo mês a partir da data.",
          "“Remover” apaga o lançamento; logo depois aparece “Desfazer”, caso tenha sido engano.",
        ],
      },
    ],
    notes: ["O resultado é uma estimativa para acompanhamento. Não é lucro contábil: não inclui impostos nem gastos que não foram lançados.", ...(has("invoices") ? ["Notas pendentes e canceladas não entram na receita."] : [])],
  }),

  reports: ({ has }) => ({
    title: "Como usar os relatórios",
    summary: "Os relatórios reúnem os dados do sistema num período, para consultar, imprimir ou enviar à contabilidade.",
    sections: [
      {
        heading: "Gerar um relatório",
        steps: [
          "Escolha o tipo de relatório e o período. O de imposto de renda pede o ano.",
          ...(has("agenda") ? ["No relatório de consultas, dá para filtrar por situação e por local."] : []),
          "Clique em “Gerar relatório”.",
          "Use “Imprimir / salvar PDF” para papel ou PDF, e “Exportar CSV” para abrir no Excel.",
        ],
      },
    ],
    notes: [
      ...(has("finance") ? ["O relatório de imposto de renda é um apoio: ele reúne os lançamentos do ano, mas não calcula imposto nem substitui a declaração."] : []),
      "Os valores dependem do que foi lançado no sistema. Confira com a contabilidade antes de usar em obrigações fiscais.",
    ],
  }),

  settings: ({ has }) => ({
    title: "Como usar as configurações",
    summary: "Aqui ficam os ajustes que valem para todo o consultório. Mudanças afetam toda a equipe.",
    sections: [
      ...(has("agenda")
        ? [{
            heading: "Locais e disponibilidade",
            steps: [
              "Use “＋ Adicionar local” para criar um local de atendimento, presencial ou remoto.",
              "Em cada local, clique em “＋ Adicionar dia”, escolha o dia da semana e informe abertura e encerramento.",
              "Clique em “Salvar horários”. A agenda passa a avisar quando uma consulta ficar fora desses horários.",
              "Um local só pode ser removido enquanto não tiver consultas.",
            ],
          }]
        : []),
      ...(has("invoices")
        ? [{
            heading: "Emissão de notas fiscais",
            steps: [
              "Em “Enviar certificado”, escolha o arquivo do certificado A1 (termina em .pfx ou .p12) e digite a senha dele. O sistema mostra o titular e a validade.",
              "Preencha os dados do prestador, o município e o código de serviço informado pela contabilidade.",
              "Escolha o ambiente do emissor. “Produção restrita” é para testes; só “Produção” gera nota com validade fiscal.",
              "Ajuste a descrição padrão do serviço. Os trechos {tipo} e {data} são preenchidos com os dados da consulta.",
              "Clique em “Salvar configuração”.",
            ],
          }]
        : []),
    ],
    notes: has("invoices") ? ["O certificado vale um ano. O sistema avisa 30 dias antes do vencimento e bloqueia a emissão quando ele vence.", "O certificado e a senha ficam guardados cifrados e não voltam a ser exibidos."] : [],
  }),
} satisfies Record<string, (context: HelpContext) => HelpTopic>;

export type HelpTopicKey = keyof typeof helpTopics;

export function resolveHelp(topic: HelpTopicKey, config: PublicAppConfig): HelpTopic {
  const terms = config.settings.terms.client;
  return helpTopics[topic]({ config, has: (feature) => config.features.includes(feature), client: terms.singular.toLowerCase(), clients: terms.plural.toLowerCase() });
}

/** Botão “Como usar” de cada tela. */
export function HelpButton({ topic, variant = "default" }: { topic: HelpTopicKey; variant?: "default" | "quiet" }) {
  const { config } = useAppConfig();
  const [open, setOpen] = useState(false);
  const help = open ? resolveHelp(topic, config) : null;
  return (
    <>
      <Button variant={variant} onClick={() => setOpen(true)}>Como usar</Button>
      {help ? (
        <Modal title={help.title} onClose={() => setOpen(false)} wide>
          <p className="ds-muted" style={{ marginBottom: 18 }}>{help.summary}</p>
          {help.sections.map((section) => <Steps key={section.heading} heading={section.heading} items={section.steps} />)}
          {help.notes?.length ? (
            <div className="ds-stack">
              {help.notes.map((note) => <Notice key={note} tone="info">{note}</Notice>)}
            </div>
          ) : null}
          <ModalFooter>
            <Button variant="primary" onClick={() => setOpen(false)}>Entendi</Button>
          </ModalFooter>
        </Modal>
      ) : null}
    </>
  );
}
